"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TJAParser = void 0;
const regex_1 = require("./common/regex");
const MetadataProperty_1 = require("./models/common/MetadataProperty");
const Course_1 = require("./models/course/Course");
const Game_1 = require("./models/song/lib/Game");
const ScoreMode_1 = require("./models/song/lib/ScoreMode");
const Side_1 = require("./models/song/lib/Side");
const Song_1 = require("./models/song/Song");
const Queue_1 = require("./utils/Queue");
const CommandType_1 = require("./models/command/lib/CommandType");
const Difficulty_1 = require("./models/course/lib/Difficulty");
const CommandFactory_1 = require("./models/command/CommandFactory");
const BranchType_1 = require("./models/command/lib/BranchType");
const Style_1 = require("./models/course/lib/Style");
const DojoGauge_1 = require("./models/course/DojoGauge");
const DojoGaugeIncrementMethod_1 = require("./models/course/lib/DojoGaugeIncrementMethod");
const DojoGaugeTotal_1 = require("./models/course/lib/DojoGaugeTotal");
/**
 * TJAParser is a class that parses a TJA file into a Song object. TJA is the
 * file format used by Taiko simulators for defining chart data.
 */
class TJAParser {
    /**
     * Takes in the contents of a TJA file, parses it line-by-line, and returns a
     * Song object. Throws if `strict` is set to `true`.
     *
     * @param raw - The contents of the TJA file.
     * @param strict - Whether the parser should throw errors on invalid data.
     * @returns - A Song object containing data about a chart file.
     */
    static parse(raw, strict = true) {
        const lines = raw.split(regex_1.LINE_REGEX).map((l) => l.trim().split('//')[0]);
        const queue = Queue_1.Queue.fromArray(lines);
        const song = new Song_1.Song();
        let isParsingCourseData = false;
        let canParseNotes = false;
        while (!queue.isEmpty) {
            const line = queue.dequeue();
            if (line === undefined || line.length === 0) {
                continue;
            }
            const params = TJAParser.parseParameter(line);
            if (params) {
                const [key, value] = params;
                if (value.length === 0) {
                    continue;
                }
                canParseNotes = false;
                if (key === MetadataProperty_1.CourseMetadataProperty.Course) {
                    isParsingCourseData = true;
                    song.courses.push(new Course_1.Course());
                }
                if (!isParsingCourseData) {
                    TJAParser.setSongData(song, key, value, strict);
                    continue;
                }
                const course = song.courses[song.courses.length - 1];
                if (typeof course === 'undefined') {
                    continue;
                }
                TJAParser.setCourseData(course, key, value, strict);
                continue;
            }
            const course = song.courses[song.courses.length - 1];
            if (typeof course === 'undefined') {
                continue;
            }
            const command = TJAParser.parseCourseCommand(course, line, canParseNotes, strict);
            if (command && command.commandType === CommandType_1.CommandType.Start) {
                canParseNotes = true;
            }
        }
        return song;
    }
    /**
     * Wrapper method for `Song#toString()` which can be used to stringify a Song
     * data object (similar to JSON's stringify method).
     *
     * @param song - The Song object to stringify.
     * @returns The TJA-compatible representation of the Song object.
     */
    static stringify(song) {
        return song.toString();
    }
    static setSongData(song, key, value, strict) {
        if (key === MetadataProperty_1.SongMetadataProperty.Title) {
            song.title = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.TitleEN) {
            song.localizedTitle.en = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.TitleJA) {
            song.localizedTitle.ja = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.TitleCN) {
            song.localizedTitle.cn = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.TitleTW) {
            song.localizedTitle.tw = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.TitleKO) {
            song.localizedTitle.ko = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.Subtitle) {
            song.subtitle = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.SubtitleEN) {
            song.localizedSubtitle.en = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.SubtitleJA) {
            song.localizedSubtitle.ja = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.SubtitleCN) {
            song.localizedSubtitle.cn = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.SubtitleTW) {
            song.localizedSubtitle.tw = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.SubtitleKO) {
            song.localizedSubtitle.ko = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.BPM) {
            const bpm = parseFloat(value);
            if (!isNaN(bpm)) {
                song.bpm = bpm;
            }
            else if (strict) {
                throw new TypeError(`Invalid BPM value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.Wave) {
            song.wave = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.Offset) {
            const offset = parseFloat(value);
            if (!isNaN(offset)) {
                song.offset = offset;
            }
            else if (strict) {
                throw new TypeError(`Invalid offset value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.DemoStart) {
            const demoStart = parseFloat(value);
            if (!isNaN(demoStart)) {
                song.demoStart = demoStart;
            }
            else if (strict) {
                throw new TypeError(`Invalid demo start value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.Genre) {
            song.genre = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.ScoreMode) {
            const mode = ScoreMode_1.ScoreMode.fromRaw(value, strict);
            if (mode) {
                song.scoreMode = mode;
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.Maker) {
            const data = regex_1.AUTHOR_REGEX.exec(value);
            if (data !== null) {
                song.maker = {
                    name: data[1],
                    url: data[2],
                };
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.Lyrics) {
            song.lyricsFilePath = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.SongVol) {
            const volume = parseInt(value, 10);
            if (!isNaN(volume)) {
                song.songVolume = volume;
            }
            else if (strict) {
                throw new TypeError(`Invalid song volume value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.SEVol) {
            const volume = parseInt(value, 10);
            if (!isNaN(volume)) {
                song.soundEffectVolume = volume;
            }
            else if (strict) {
                throw new TypeError(`Invalid sound effect volume value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.Side) {
            const side = Side_1.Side.fromRaw(value, strict);
            if (side) {
                song.side = side;
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.Life) {
            const life = parseInt(value, 10);
            if (!isNaN(life)) {
                song.life = life;
            }
            else if (strict) {
                throw new TypeError(`Invalid life value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.Game) {
            const game = Game_1.Game.fromRaw(value, strict);
            if (game) {
                song.game = game;
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.HeadScroll) {
            const headScroll = parseInt(value, 10);
            if (!isNaN(headScroll)) {
                song.headScroll = headScroll;
            }
            else if (strict) {
                throw new TypeError(`Invalid head scroll value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.BGImage) {
            song.backgroundImagePath = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.BGMovie) {
            song.backgroundMoviePath = value;
            return;
        }
        if (key === MetadataProperty_1.SongMetadataProperty.MovieOffset) {
            const movieOffset = parseInt(value, 10);
            if (!isNaN(movieOffset)) {
                song.movieOffset = movieOffset;
            }
            else if (strict) {
                throw new TypeError(`Invalid movie offset value: ${value}`);
            }
            return;
        }
    }
    static setCourseData(course, key, value, strict) {
        if (key === MetadataProperty_1.CourseMetadataProperty.Course) {
            const difficulty = Difficulty_1.Difficulty.fromRaw(value, strict);
            if (difficulty) {
                course.difficulty = difficulty;
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.Level) {
            const level = parseInt(value, 10);
            if (!isNaN(level)) {
                course.stars = level;
            }
            else if (strict) {
                throw new TypeError(`Invalid course level value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.ScoreInit) {
            const scoreInit = parseInt(value, 10);
            if (!isNaN(scoreInit)) {
                course.activeCourse.scoreInit = scoreInit;
            }
            else if (strict) {
                throw new TypeError(`Invalid course score init value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.ScoreDiff) {
            const scoreDiff = parseInt(value, 10);
            if (!isNaN(scoreDiff)) {
                course.activeCourse.scoreDiff = scoreDiff;
            }
            else if (strict) {
                throw new TypeError(`Invalid course score diff value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.Balloon) {
            const counts = value
                .split(',')
                .filter((c) => c.trim().length > 0)
                .map((c) => parseInt(c.trim(), 10));
            const filteredCounts = counts.filter((c) => !isNaN(c));
            if (filteredCounts.length === counts.length) {
                course.activeCourse.balloonCounts = filteredCounts;
            }
            else if (strict) {
                throw new TypeError(`Invalid course balloon value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.BalloonNor) {
            const counts = value.split(',').map((c) => parseInt(c.trim(), 10));
            const filteredCounts = counts.filter((c) => !isNaN(c));
            if (filteredCounts.length === counts.length) {
                course.activeCourse.branchBalloonCounts[BranchType_1.BranchType.Normal] =
                    filteredCounts;
            }
            else if (strict) {
                throw new TypeError(`Invalid course balloon (normal) value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.BalloonExp) {
            const counts = value.split(',').map((c) => parseInt(c.trim(), 10));
            const filteredCounts = counts.filter((c) => !isNaN(c));
            if (filteredCounts.length === counts.length) {
                course.activeCourse.branchBalloonCounts[BranchType_1.BranchType.Advanced] =
                    filteredCounts;
            }
            else if (strict) {
                throw new TypeError(`Invalid course balloon (advanced) value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.BalloonMas) {
            const counts = value.split(',').map((c) => parseInt(c.trim(), 10));
            const filteredCounts = counts.filter((c) => !isNaN(c));
            if (filteredCounts.length === counts.length) {
                course.activeCourse.branchBalloonCounts[BranchType_1.BranchType.Master] =
                    filteredCounts;
            }
            else if (strict) {
                throw new TypeError(`Invalid course balloon (master) value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.Style) {
            const style = Style_1.Style.fromRaw(value, strict);
            if (style) {
                course.activeCourseStyle = style;
                course.activeCourse.style = style;
            }
            return;
        }
        if ([
            MetadataProperty_1.CourseMetadataProperty.Exam1,
            MetadataProperty_1.CourseMetadataProperty.Exam2,
            MetadataProperty_1.CourseMetadataProperty.Exam3,
        ].includes(key)) {
            const index = parseInt(key.slice(-1), 10) - 1;
            const gauge = DojoGauge_1.DojoGauge.parse(value, strict);
            if (gauge) {
                course.setGauge(index, gauge);
            }
            else if (strict) {
                throw new TypeError(`Invalid gauge data: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.GaugeIncr) {
            const gaugeIncrementMethod = DojoGaugeIncrementMethod_1.DojoGaugeIncrementMethod.fromRaw(value, strict);
            if (gaugeIncrementMethod) {
                course.activeCourse.gaugeIncrementMethod = gaugeIncrementMethod;
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.Total) {
            const rawTotal = parseInt(value, 10);
            if (!isNaN(rawTotal)) {
                const total = new DojoGaugeTotal_1.DojoGaugeTotal(rawTotal);
                course.activeCourse.gaugeTotal = total;
            }
            else if (strict) {
                throw new TypeError(`Invalid gauge total value: ${value}`);
            }
            return;
        }
        if (key === MetadataProperty_1.CourseMetadataProperty.HiddenBranch && value === '1') {
            course.activeCourse.hiddenBranches = true;
            return;
        }
    }
    static parseCourseCommand(course, line, canParseNotes, strict) {
        const command = CommandFactory_1.CommandFactory.fromLine(line, canParseNotes, strict);
        if (typeof command !== 'undefined') {
            course.addCommand(command);
            return command;
        }
    }
    static parseParameter(line) {
        const components = line.split(':').map((c) => c.trim());
        if (components.length < 2) {
            return undefined;
        }
        const key = components[0];
        const value = components.slice(1).join(':');
        return [key, value];
    }
}
exports.TJAParser = TJAParser;
